import { Check } from 'lucide-react';
import { asId } from '@obraplan/core/domain/shared/ids';
import { roundPrice } from '@obraplan/core/domain/takeoff/material-price';
import { steelGaugeLabel } from '@obraplan/core/domain/takeoff/steel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { NumeroOpcionalInput } from './campos';
import type {
  EscolhasParaOrcar,
  LinhaDeUnidade,
  LinhaDoAco,
  MaoDeObraSemFuncao,
  PassoParaOrcar,
  PedidoParaOrcar,
  ProdutoSemCodigo,
} from './completar-para-orcar';
import { precoUnitario } from './formato';
import { textoDoFator } from './precos-da-obra';
import type { EstadoDaConsulta } from './use-completar-para-orcar';

/**
 * Completar para orçar, na prévia da planilha por peça — uma vez por produto (13/10/2026).
 *
 * Quatro grupos, cada um só quando tem linha: o aço a conferir, a unidade diferente, o material
 * sem código e a mão de obra sem função. A regra do que perguntar e do que gravar mora em
 * `completar-para-orcar.ts`; aqui é a tela, e as respostas sobem para a importação, que as grava
 * na confirmação — junto com o catálogo, e nunca antes dele.
 */

const SELECT_CLASS =
  'h-11 min-w-0 max-w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

const quantos = (n: number, um: string, varios: string): string =>
  `${String(n)} ${n === 1 ? um : varios}`;

const quilos = (valor: number): string =>
  `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;

export function CompletarParaOrcar({
  passo,
  estado,
  aPerguntar,
  escolhas,
  onEscolhas,
  pedido,
  ligadas,
}: {
  passo: PassoParaOrcar;
  estado: EstadoDaConsulta;
  /** Quantos materiais a importação traz para perguntar ao servidor — o que a leitura diz. */
  aPerguntar: number;
  escolhas: EscolhasParaOrcar;
  onEscolhas: (mudar: (atual: EscolhasParaOrcar) => EscolhasParaOrcar) => void;
  /** O que a confirmação vai gravar, já calculado — a linha de baixo o diz. */
  pedido: PedidoParaOrcar;
  /** Em quantos serviços cada descrição de mão de obra ficou ligada, na mescla de agora. */
  ligadas: ReadonlyMap<string, number>;
}) {
  const lendo = estado === 'LENDO' && aPerguntar > 0;
  const temLinha =
    passo.aco.length +
      passo.acoConfirmado.length +
      passo.unidade.length +
      passo.semCodigo.length +
      passo.maoDeObra.length >
    0;
  const temAviso = passo.semCompras !== undefined || passo.semPrecos !== undefined;
  if (!lendo && !temLinha && !temAviso) return null;

  const funcoesEscolhidas = passo.maoDeObra.filter((linha) =>
    escolhas.funcoes.has(linha.chave),
  ).length;
  const vaiGravar = [
    pedido.fatores.length > 0
      ? `${quantos(pedido.fatores.length, 'fator', 'fatores')} de produto, para todas as obras`
      : undefined,
    pedido.provisorios.length > 0
      ? `${quantos(pedido.provisorios.length, 'provisório', 'provisórios')} nesta obra`
      : undefined,
    funcoesEscolhidas > 0
      ? `${quantos(funcoesEscolhidas, 'função escolhida', 'funções escolhidas')} na mão de obra`
      : undefined,
  ].filter((parte) => parte !== undefined);

  return (
    <section
      aria-label="Completar para orçar"
      className="flex flex-col gap-4 rounded-md border border-(--color-border-subtle) p-3"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-medium">Completar para orçar — uma vez por produto</h3>
        <p className="text-xs text-(--color-content-muted)">
          O que falta para o orçamento ter preço, perguntado uma vez por produto, e não linha a
          linha nem obra a obra: o fator vale para todas as obras; o provisório, só para esta. Tudo
          é opcional — o que ficar em branco não é gravado, e pode completar depois no preço do
          item.
        </p>
      </div>

      {lendo && (
        <p aria-live="polite" className="text-sm text-(--color-content-muted)">
          {`Lendo no compras a unidade e o médio de ${quantos(aPerguntar, 'material', 'materiais')}, e os fatores já confirmados…`}
        </p>
      )}

      {passo.semCompras !== undefined && (
        <p className="text-xs text-(--color-content-muted)">{passo.semCompras}</p>
      )}

      {(passo.aco.length > 0 || passo.acoConfirmado.length > 0) && (
        <GrupoDoAco
          linhas={passo.aco}
          confirmadas={passo.acoConfirmado}
          conferidos={escolhas.conferidos}
          onConferir={(chaves, marcar) =>
            onEscolhas((atual) => {
              const conferidos = new Set(atual.conferidos);
              for (const chave of chaves) {
                if (marcar) conferidos.add(chave);
                else conferidos.delete(chave);
              }
              return { ...atual, conferidos };
            })
          }
        />
      )}

      {passo.unidade.length > 0 && (
        <GrupoDaUnidade
          linhas={passo.unidade}
          comFator={passo.unidadeComFator}
          fatores={escolhas.fatores}
          onFator={(chave, fator) =>
            onEscolhas((atual) => {
              const fatores = new Map(atual.fatores);
              if (fator === undefined) fatores.delete(chave);
              else fatores.set(chave, fator);
              return { ...atual, fatores };
            })
          }
        />
      )}

      {passo.semPrecos !== undefined && (
        <p className="text-xs text-(--color-content-muted)">{passo.semPrecos}</p>
      )}

      {passo.semCodigo.length > 0 && (
        <GrupoSemCodigo
          produtos={passo.semCodigo}
          comProvisorio={passo.semCodigoComProvisorio}
          provisorios={escolhas.provisorios}
          onProvisorio={(key, preco) =>
            onEscolhas((atual) => {
              const provisorios = new Map(atual.provisorios);
              if (preco === undefined) provisorios.delete(key);
              else provisorios.set(key, preco);
              return { ...atual, provisorios };
            })
          }
        />
      )}

      {passo.maoDeObra.length > 0 && (
        <GrupoDaMaoDeObra
          linhas={passo.maoDeObra}
          escolhidas={escolhas.funcoes}
          ligadas={ligadas}
          onFuncao={(chave, funcao) =>
            onEscolhas((atual) => {
              const funcoes = new Map(atual.funcoes);
              if (funcao === undefined) funcoes.delete(chave);
              else funcoes.set(chave, asId<'JobRole'>(funcao));
              return { ...atual, funcoes };
            })
          }
        />
      )}

      {temLinha && (
        <p className="text-xs text-(--color-content-muted)" data-vai-gravar>
          {vaiGravar.length === 0
            ? 'Nada respondido ainda: a importação entra do mesmo jeito, e o que faltar se completa no preço do item.'
            : `Na confirmação, junto com a importação: ${vaiGravar.join(' · ')}.`}
        </p>
      )}
    </section>
  );
}

// ── O aço ───────────────────────────────────────────────────────────────────────

/**
 * O vergalhão em kg contra o compras em barra: a sugestão da barra de 12 m, linha a linha, e a
 * marca de quem conferiu. Sem a marca, nada é gravado — a sugestão deixou de valer sozinha.
 */
function GrupoDoAco({
  linhas,
  confirmadas,
  conferidos,
  onConferir,
}: {
  linhas: readonly LinhaDoAco[];
  confirmadas: readonly LinhaDoAco[];
  conferidos: ReadonlySet<string>;
  onConferir: (chaves: readonly string[], marcar: boolean) => void;
}) {
  const todos = linhas.length > 0 && linhas.every((linha) => conferidos.has(linha.produto.chave));
  return (
    <section aria-label="Aço: o fator da barra" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium">Aço: o fator da barra, conferido</h4>
        {linhas.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() =>
              onConferir(
                linhas.map((linha) => linha.produto.chave),
                !todos,
              )
            }
          >
            {todos ? 'Desmarcar todos' : 'Conferir todos'}
          </Button>
        )}
      </div>
      {linhas.length > 0 && (
        <>
          <p className="text-xs text-(--color-content-muted)">
            O compras vende o vergalhão por barra, e o orçamento conta o quilo. A sugestão é a barra
            de 12 m da bitola (kg/m × 12 m): confira e marque. O que não for marcado não é gravado —
            barra de outro comprimento, deixe sem marcar e digite o fator no preço do item.
          </p>
          <TableWrapper className="max-h-72 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Bitola</TableHead>
                  <TableHead className="text-right">Barra de 12 m</TableHead>
                  <TableHead className="text-right">Médio no compras</TableHead>
                  <TableHead className="text-right">Fator</TableHead>
                  <TableHead className="text-right">Preço por kg</TableHead>
                  <TableHead className="text-center">Conferido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map((linha) => (
                  <LinhaDeAco
                    key={linha.produto.chave}
                    linha={linha}
                    conferido={conferidos.has(linha.produto.chave)}
                    onConferir={(marcar) => onConferir([linha.produto.chave], marcar)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </>
      )}
      {confirmadas.length > 0 && (
        <p className="flex flex-wrap items-center gap-1 text-xs text-(--color-content-muted)">
          <Check className="size-3.5" aria-hidden="true" />
          {`Já confirmado (vale para todas as obras): ${confirmadas
            .map(
              (linha) =>
                `${linha.produto.description} — 1 ${linha.produto.unit} = ${textoDoFator(
                  linha.confirmado?.factor ?? linha.fator,
                )} ${linha.unidadeDoCompras}`,
            )
            .join('; ')}.`}
        </p>
      )}
    </section>
  );
}

function LinhaDeAco({
  linha,
  conferido,
  onConferir,
}: {
  linha: LinhaDoAco;
  conferido: boolean;
  onConferir: (marcar: boolean) => void;
}) {
  const { produto } = linha;
  return (
    <TableRow>
      <TableCell>
        <span className="block font-medium">{produto.description}</span>
        <span className="font-mono text-xs text-(--color-content-muted)">{produto.code}</span>
      </TableCell>
      <TableCell className="text-xs">{steelGaugeLabel(linha.bitola)}</TableCell>
      <TableCell className="tabular text-right">{quilos(linha.kgPorBarra)}</TableCell>
      <TableCell className="tabular text-right">
        {linha.medio === undefined ? (
          <span className="text-xs text-(--color-content-muted)">sem compra no período</span>
        ) : (
          `${precoUnitario(linha.medio)}/${linha.unidadeDoCompras}`
        )}
      </TableCell>
      <TableCell className="tabular text-right text-xs">
        {`1 ${produto.unit} = ${textoDoFator(linha.fator)} ${linha.unidadeDoCompras}`}
      </TableCell>
      <TableCell className="tabular text-right">
        {linha.precoPorUnidadeDoItem === undefined
          ? '—'
          : `${precoUnitario(linha.precoPorUnidadeDoItem)}/${produto.unit}`}
      </TableCell>
      <TableCell className="text-center">
        <input
          type="checkbox"
          aria-label={`Conferido: ${produto.description}`}
          checked={conferido}
          onChange={(event) => onConferir(event.target.checked)}
        />
      </TableCell>
    </TableRow>
  );
}

// ── A unidade diferente ─────────────────────────────────────────────────────────

function GrupoDaUnidade({
  linhas,
  comFator,
  fatores,
  onFator,
}: {
  linhas: readonly LinhaDeUnidade[];
  comFator: number;
  fatores: ReadonlyMap<string, number>;
  onFator: (chave: string, fator: number | undefined) => void;
}) {
  return (
    <section aria-label="Unidade diferente" className="flex flex-col gap-2">
      <h4 className="text-sm font-medium">Unidade diferente: o fator, uma vez por produto</h4>
      <p className="text-xs text-(--color-content-muted)">
        O compras vende estes produtos noutra unidade. O fator diz quantas unidades do compras cabem
        numa do item — "1 L = 0,0556 UN" é o balde de 18 L —, e vale para todas as obras.
        {comFator > 0 &&
          ` ${quantos(comFator, 'produto de unidade diferente já tem', 'produtos de unidade diferente já têm')} o fator, e não ${comFator === 1 ? 'aparece' : 'aparecem'} aqui.`}
      </p>
      <TableWrapper className="max-h-72 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead>Fator</TableHead>
              <TableHead className="text-right">Médio no compras</TableHead>
              <TableHead className="text-right">Preço por unidade do item</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linhas.map((linha) => {
              const { produto } = linha;
              const fator = fatores.get(produto.chave);
              const valido = fator !== undefined && Number.isFinite(fator) && fator > 0;
              return (
                <TableRow key={produto.chave}>
                  <TableCell>
                    <span className="block font-medium">{produto.description}</span>
                    <span className="font-mono text-xs text-(--color-content-muted)">
                      {produto.code}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-1.5 text-sm">
                      <span className="tabular">{`1 ${produto.unit} =`}</span>
                      <NumeroOpcionalInput
                        aria-label={`Fator de ${produto.description}: quantos ${linha.unidadeDoCompras} em 1 ${produto.unit}`}
                        placeholder="fator"
                        className="h-9 w-24 text-right"
                        value={fator}
                        onChange={(valor) => onFator(produto.chave, valor)}
                      />
                      <span>{linha.unidadeDoCompras}</span>
                    </span>
                    {fator !== undefined && !valido && (
                      <span className="text-[11px] text-(--color-state-warn)">
                        o fator é maior que zero
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {linha.medio === undefined ? (
                      <span className="text-xs text-(--color-content-muted)">
                        sem compra no período
                      </span>
                    ) : (
                      `${precoUnitario(linha.medio)}/${linha.unidadeDoCompras}`
                    )}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {linha.medio === undefined || !valido
                      ? '—'
                      : `${precoUnitario(roundPrice(linha.medio * fator))}/${produto.unit}`}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </section>
  );
}

// ── Sem código ──────────────────────────────────────────────────────────────────

function GrupoSemCodigo({
  produtos,
  comProvisorio,
  provisorios,
  onProvisorio,
}: {
  produtos: readonly ProdutoSemCodigo[];
  comProvisorio: number;
  provisorios: ReadonlyMap<string, number>;
  onProvisorio: (key: string, preco: number | undefined) => void;
}) {
  return (
    <section aria-label="Sem código de compras" className="flex flex-col gap-2">
      <h4 className="text-sm font-medium">Sem código: o preço provisório, só nesta obra</h4>
      <p className="text-xs text-(--color-content-muted)">
        Sem código de compras não há médio a perguntar, e o provisório é o preço. Ele vale só nesta
        obra: a areia sem código daqui não é, pelo nome, a areia da outra.
        {comProvisorio > 0 &&
          ` ${quantos(comProvisorio, 'material sem código já tem', 'materiais sem código já têm')} provisório nesta obra.`}
      </p>
      <TableWrapper className="max-h-72 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Material</TableHead>
              <TableHead>Preço provisório</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {produtos.map((produto) => {
              const preco = provisorios.get(produto.key);
              return (
                <TableRow key={produto.key}>
                  <TableCell className="font-medium">{produto.description}</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <NumeroOpcionalInput
                        aria-label={`Preço provisório de ${produto.description}`}
                        placeholder="R$"
                        className="h-9 w-32 text-right"
                        value={preco}
                        onChange={(valor) => onProvisorio(produto.key, valor)}
                      />
                      <span className="text-xs text-(--color-content-muted)">
                        {produto.unit === undefined ? 'R$' : `R$/${produto.unit}`}
                      </span>
                    </span>
                    {preco !== undefined && preco < 0 && (
                      <span className="text-[11px] text-(--color-state-warn)">
                        o preço não pode ser negativo
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </section>
  );
}

// ── A mão de obra ───────────────────────────────────────────────────────────────

function GrupoDaMaoDeObra({
  linhas,
  escolhidas,
  ligadas,
  onFuncao,
}: {
  linhas: readonly MaoDeObraSemFuncao[];
  escolhidas: EscolhasParaOrcar['funcoes'];
  ligadas: ReadonlyMap<string, number>;
  onFuncao: (chave: string, funcao: string | undefined) => void;
}) {
  return (
    <section aria-label="Mão de obra sem função" className="flex flex-col gap-2">
      <h4 className="text-sm font-medium">Mão de obra sem função</h4>
      <p className="text-xs text-(--color-content-muted)">
        A descrição não casou pelo nome com nenhuma função permitida no serviço. A função escolhida
        vale para a descrição em todos os serviços desta planilha onde ela é permitida — e é ela que
        dá o R$/h da folha.
      </p>
      <TableWrapper className="max-h-72 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Serviços</TableHead>
              <TableHead>Função</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linhas.map((linha) => {
              const escolhida = escolhidas.get(linha.chave);
              const quantas = ligadas.get(linha.chave) ?? 0;
              return (
                <TableRow key={linha.chave}>
                  <TableCell className="font-medium">{linha.description}</TableCell>
                  <TableCell className="text-xs text-(--color-content-muted)">
                    {linha.servicos.map((servico) => servico.rotulo).join(' · ')}
                  </TableCell>
                  <TableCell>
                    {linha.funcoes.length === 0 ? (
                      <span className="text-xs text-(--color-content-muted)">
                        nenhuma função permitida nestes serviços — cadastre na Estrutura
                      </span>
                    ) : (
                      <span className="flex flex-col gap-1">
                        <select
                          aria-label={`Função de ${linha.description}`}
                          className={SELECT_CLASS}
                          value={escolhida ?? ''}
                          onChange={(event) =>
                            onFuncao(
                              linha.chave,
                              event.target.value === '' ? undefined : event.target.value,
                            )
                          }
                        >
                          <option value="">Sem função — escolher depois</option>
                          {linha.funcoes.map((funcao) => (
                            <option key={funcao.id} value={funcao.id}>
                              {funcao.name}
                            </option>
                          ))}
                        </select>
                        {escolhida !== undefined && (
                          <Badge
                            tone={quantas === linha.servicos.length ? 'ok' : 'warn'}
                            className="self-start"
                          >
                            {quantas === linha.servicos.length
                              ? linha.servicos.length === 1
                                ? 'ligada'
                                : `ligada nos ${String(linha.servicos.length)} serviços`
                              : quantas === 0
                                ? // A função que outro item do serviço já usa não se repete (RN-252).
                                  'não ligou: a função já é de outro item, ou não é permitida aqui'
                                : `ligada em ${String(quantas)} de ${String(linha.servicos.length)} serviços`}
                          </Badge>
                        )}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </section>
  );
}
